import { onAuthStateChanged, User } from "firebase/auth";
import { Firestore } from "firebase/firestore";
import { APP_VERSION } from "@/version";
import { listAllQuizzes, listMyQuizzes } from "@/data/quizzes";
import { isDebugEnabled } from "@/lib/debugFlag";

export type RuntimeInfo = {
  projectId: string;
  apiKeyTail: string;
  authDomain: string;
  uid: string | null;
  email: string | null;
  anonymous: boolean;
  dbHost: string;
  usingEmulator: boolean;
  quizCountMine: number | null;
  quizCountAll: number | null;
  appVersion: string;
  shell: "web" | "office";
  isAdmin: boolean;
  authReady: boolean;
  subscribed: boolean;
};

type Listener = (info: RuntimeInfo) => void;

let current: RuntimeInfo = {
  projectId: "",
  apiKeyTail: "",
  authDomain: "",
  uid: null,
  email: null,
  anonymous: false,
  dbHost: "",
  usingEmulator: false,
  quizCountMine: null,
  quizCountAll: null,
  appVersion: APP_VERSION,
  shell: typeof (globalThis as any).Office !== "undefined" ? "office" : "web",
  isAdmin: false,
  authReady: false,
  subscribed: false,
};

const listeners = new Set<Listener>();
export function subscribeRuntimeInfo(fn: Listener) {
  listeners.add(fn);
  fn(current);
  return () => {
    listeners.delete(fn);
  };
}
function emit() {
  for (const l of listeners) l(current);
  (globalThis as any).qq = { ...(globalThis as any).qq, runtime: current };
  if (isDebugEnabled()) {
    console.debug('[firebaseDebug]', {
      authReady: current.authReady,
      subscribed: current.subscribed,
      quizzesCount: current.quizCountMine ?? 0,
    });
  }
}

export function initRuntimeInfo(opts: {
  projectId: string;
  apiKey: string;
  authDomain: string;
  db: Firestore;
  dbHost: string;
  usingEmulator: boolean;
  auth: import("firebase/auth").Auth;
}) {
  const { projectId, apiKey, authDomain, db, dbHost, usingEmulator, auth } = opts;
  current = {
    ...current,
    projectId,
    apiKeyTail: apiKey ? apiKey.slice(-8) : "",
    authDomain,
    dbHost,
    usingEmulator,
  };
  emit();

  // Auth stream
  onAuthStateChanged(auth, (user: User | null) => {
    current = {
      ...current,
      uid: user?.uid ?? null,
      email: user?.email ?? null,
      anonymous: !!user?.isAnonymous,
    };
    emit();
    // Refresh quiz count after auth is available
    refreshQuizCounts(db, user?.uid ?? undefined).catch(() => {});
  });

  // Initial count for all quizzes only (user quizzes will load after auth)
  refreshQuizCounts(db, undefined).catch(() => {});
}

export async function refreshQuizCounts(db: Firestore, uid?: string) {
  try {
    const all = await listAllQuizzes(db);
    // Only fetch user's quizzes if we have a valid uid
    const mine = uid ? await listMyQuizzes(db, uid) : [];
    current = { ...current, quizCountMine: mine.length, quizCountAll: all.length };
    if (isDebugEnabled()) {
      console.debug('[Debug] allIds:', all.map(q => q.id), 'myIds:', mine.map(q => q.id));
    }
  } catch {
    current = { ...current, quizCountMine: null, quizCountAll: null };
  }
  emit();
}

export function getRuntimeInfo(): RuntimeInfo {
  return current;
}

export function setRuntimeIsAdmin(isAdmin: boolean) {
  current = { ...current, isAdmin };
  if (isDebugEnabled()) {
    const { shell, projectId, authDomain, uid } = current;
    console.debug('[QuickQuiz]', { v: APP_VERSION, shell, proj: projectId, authDomain, uid, isAdmin });
  }
  emit();
}

export function setRuntimeSubscribed(flag: boolean) {
  current = { ...current, subscribed: flag };
  emit();
}

export function setRuntimeAuthReady(flag: boolean) {
  current = { ...current, authReady: flag };
  emit();
}


